import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WaitlistForm } from '../components/WaitlistForm';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SignalLogo } from '../components/SignalLogo';
import { track } from '../lib/analytics';
import { cn } from '../lib/utils';

export function LandingPage() {
  const { t } = useTranslation('landing');
  const { t: tc } = useTranslation('common');

  useEffect(() => {
    track('page_viewed', { page: 'landing' });
  }, []);

  const scrollToWaitlist = () => {
    track('waitlist_cta_clicked');
    document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-brand-canvas">
      {/* Navigation Bar */}
      <nav className="fixed left-0 right-0 top-0 z-[100] flex h-[52px] items-center justify-between border-b border-brand-border bg-white/80 px-6 backdrop-blur-[20px]">
        <span className="flex items-center gap-2">
          <SignalLogo size={24} />
          <span className="text-[17px] font-semibold leading-6 tracking-[-0.02em] text-brand-text">
            {tc('signal')}
          </span>
        </span>
        <div className="flex items-center gap-3.5">
          <LanguageSwitcher />
          <button
            onClick={scrollToWaitlist}
            className="inline-flex h-8 cursor-pointer items-center rounded-full border-none bg-accent-blue px-4 text-[13px] font-medium leading-5 text-white hover:bg-accent-blue-hover"
          >
            {t('nav.joinWaitlist')}
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pb-20 pt-[120px] text-center">
        <div aria-hidden className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute -left-40 -top-[220px] h-[640px] w-[640px] rounded-full bg-accent-blue opacity-35 blur-[110px]" />
          <div className="absolute -right-[200px] -top-[100px] h-[560px] w-[560px] rounded-full bg-accent-purple opacity-30 blur-[110px]" />
          <div className="absolute -bottom-[260px] left-1/2 h-[560px] w-[820px] -translate-x-1/2 rounded-full bg-accent-cyan opacity-[0.28] blur-[120px]" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(251,251,253,0)_0%,#FBFBFD_92%)]" />
        </div>
        <div className="relative z-[1] w-full max-w-[760px]">
          <div className="flex flex-col gap-8">
            <div>
              <div className="mb-14 inline-flex rounded-full bg-white/70 px-[15px] py-1.5">
                <span className="text-[13px] font-medium leading-5 text-brand-text">
                  {t('hero.eyebrow')}
                </span>
              </div>

              <h1 className="mb-6 text-[40px] font-semibold leading-[1.05] tracking-[-0.02em] text-brand-text md:text-[76px]">
                <span className="whitespace-nowrap">{t('hero.title.line1')}</span>
                <br />
                {t('hero.title.line2')}
              </h1>

              <p className="mx-auto max-w-[600px] text-xl font-normal leading-[1.5] text-brand-subtle">
                {t('hero.subtitle')}
              </p>
            </div>

            <div className="flex flex-col items-center justify-center gap-3.5 md:flex-row md:flex-wrap">
              <button
                onClick={scrollToWaitlist}
                className="inline-flex h-[52px] cursor-pointer items-center rounded-full border-none bg-accent-blue px-[30px] text-base font-medium leading-6 text-white hover:bg-accent-blue-hover"
              >
                {t('hero.cta')}
              </button>
              <button
                onClick={scrollToHowItWorks}
                className="inline-flex h-[52px] cursor-pointer items-center border-none bg-transparent px-5 text-base font-medium leading-6 text-brand-text hover:text-accent-blue"
              >
                {t('hero.learnMore')}
              </button>
            </div>

            <div className="w-full pt-16">
              <img
                src="/images/hero-illustration.svg"
                alt=""
                className="block h-auto w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-brand-surface px-6 py-24">
        <div className="mx-auto max-w-[1080px]">
          <h2 className="mb-16 text-center text-[28px] font-semibold leading-10 tracking-[-0.01em] text-brand-text md:text-4xl">
            {t('howItWorks.title')}
          </h2>

          <div className="relative">
            <div className="grid grid-cols-1 gap-12 text-center md:grid-cols-3">
              {[
                {
                  step: t('howItWorks.step1.step'),
                  title: t('howItWorks.step1.title'),
                  desc: t('howItWorks.step1.desc'),
                  icon: '/images/step1-icon.svg',
                  tint: 'bg-accent-blue-light',
                },
                {
                  step: t('howItWorks.step2.step'),
                  title: t('howItWorks.step2.title'),
                  desc: t('howItWorks.step2.desc'),
                  icon: '/images/step2-icon.svg',
                  tint: 'bg-accent-purple-light',
                },
                {
                  step: t('howItWorks.step3.step'),
                  title: t('howItWorks.step3.title'),
                  desc: t('howItWorks.step3.desc'),
                  icon: '/images/step3-icon.svg',
                  tint: 'bg-accent-green-light',
                },
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col items-center">
                  <div
                    className={cn(
                      'mb-5 flex h-14 w-14 items-center justify-center rounded-2xl',
                      item.tint,
                    )}
                  >
                    <img src={item.icon} alt="" className="h-6 w-6" />
                  </div>
                  <span className="mb-2 text-[13px] font-normal leading-5 text-brand-muted">
                    {item.step}
                  </span>
                  <h3 className="mb-2.5 text-[19px] font-semibold leading-7 tracking-[-0.01em] text-brand-text">
                    {item.title}
                  </h3>
                  <p className="max-w-[373px] text-[15px] font-normal leading-[1.5] text-brand-subtle">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For (Personas) */}
      <section className="px-6 py-24">
        <div className="mx-auto max-w-[1080px]">
          <h2 className="mx-auto mb-16 max-w-[640px] text-center text-[28px] font-semibold leading-10 tracking-[-0.01em] text-brand-text md:text-4xl">
            {t('whoItsFor.title')}
          </h2>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Developer Card */}
            <div className="rounded-[20px] border border-brand-border bg-[linear-gradient(135deg,rgba(0,113,227,0.08),rgba(175,82,222,0.08))] p-6 md:p-9">
              <div className="mb-5 flex items-center gap-4">
                <img src="/images/dev-icon.svg" alt="" className="h-10 w-10" />
                <span className="inline-flex rounded-full bg-white/70 px-3.5 py-[5px] text-[13px] font-medium leading-5 text-brand-text">
                  {t('whoItsFor.devs.tag')}
                </span>
              </div>
              <h3 className="mb-2.5 text-2xl font-semibold leading-8 tracking-[-0.01em] text-brand-text">
                {t('whoItsFor.devs.title')}
              </h3>
              <p className="text-base font-normal leading-[1.55] text-brand-subtle">
                {t('whoItsFor.devs.desc')}
              </p>
            </div>

            {/* Finance Card */}
            <div className="rounded-[20px] border border-brand-border bg-[linear-gradient(135deg,rgba(52,199,89,0.08),rgba(0,199,190,0.08))] p-6 md:p-9">
              <div className="mb-5 flex items-center gap-4">
                <img src="/images/finance-icon.svg" alt="" className="h-10 w-10" />
                <span className="inline-flex rounded-full bg-white/70 px-3.5 py-[5px] text-[13px] font-medium leading-5 text-brand-text">
                  {t('whoItsFor.finance.tag')}
                </span>
              </div>
              <h3 className="mb-2.5 text-2xl font-semibold leading-8 tracking-[-0.01em] text-brand-text">
                {t('whoItsFor.finance.title')}
              </h3>
              <p className="text-base font-normal leading-[1.55] text-brand-subtle">
                {t('whoItsFor.finance.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Form Section */}
      <section id="waitlist" className="bg-brand-surface px-6 py-24">
        <div className="mx-auto max-w-[600px]">
          <div className="mb-10 text-center">
            <h2 className="mb-4 text-[28px] font-semibold leading-10 tracking-[-0.01em] text-brand-text md:text-4xl">
              {t('earlyAccess.title')}
            </h2>
            <p className="text-[17px] font-normal leading-7 text-brand-subtle">
              {t('earlyAccess.subtitle')}
            </p>
          </div>
          <WaitlistForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-brand-border px-6 py-8">
        <div className="mx-auto flex max-w-[1080px] items-center justify-between">
          <p className="text-[13px] font-normal leading-5 text-brand-muted">
            {t('footer.copyright')}
          </p>
          <LanguageSwitcher />
        </div>
      </footer>
    </div>
  );
}
